import { createContext, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'

// Utils
import { readCookie, createCookie } from '../utils/store/cookies'
import { UserContext } from './UserContext'

// example iOS Legacy app
// Mozilla/5.0 (iPad; CPU iPhone OS 13_2_3 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.0.3 Mobile/15E148 Safari/604.1 RINUSAPP

// Example Latest
// Mozilla/5.0 (iPad; CPU iPhone OS 13_2_3 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.0.3 Mobile/15E148 Safari/604.1 RINUSAPP-V3-IOS RINUSAPP-COOKIECONSENT-REJECTED

export const AppContext = createContext()

const keyAppNative = 'RINUSAPP'
const keyAppVersionIos = 'RINUSAPP-V3-IOS'
const keyAppVersionAndroid = 'RINUSAPP-V3-ANDROID'
const keyCookieConsentRejected = 'RINUSAPP-COOKIECONSENT-REJECTED'

const AppProvider = (props) => {
  const { children, loggedIn } = props

  const router = useRouter()

  const UserState = useContext(UserContext)

  const [contextMounted, setContextMounted] = useState(false)
  const [isNativeApp, setIsNativeApp] = useState(false)
  const [isLegacyAndroidApp, setIsLegacyAndroidApp] = useState(false)
  const [isLegacyiOSApp, setIsLegacyiOSApp] = useState(false)
  const [isLatestAndroidApp, setIsLatestAndroidApp] = useState(false)
  const [isLatestiOSApp, setIsLatestiOSApp] = useState(true)
  const [isSafari, setIsSafari] = useState(false)
  const [isCookieConsentRejected, setIsCookieConsentRejected] = useState(false)

  // Application is restricted and contains a login wall
  const [isRestricted, setIsRestricted] = useState(
    process.env.NEXT_PUBLIC_LOGIN_WALL === 'true' && (loggedIn || !UserState.loggedIn)
  )
  // Excluded pages from restrictions
  const excluded = process.env.NEXT_PUBLIC_LOGIN_WALL_EXCLUDED.split(',') || ['/']
  const [isExcluded, setIsExcluded] = useState(excluded.indexOf(decodeURIComponent(router.asPath)) > -1)

  // Detect excluded page is
  useEffect(() => {
    if (!isRestricted) return false
    setIsExcluded(excluded.indexOf(decodeURIComponent(router.asPath)) > -1)
    if (UserState.loggedIn) setIsRestricted(false)
  }, [isRestricted, router, setIsExcluded, UserState.loggedIn])

  useEffect(() => {
    if (contextMounted) return false
    const agent = navigator ? navigator?.userAgent : false
    setIsNativeApp(
      agent &&
        (agent.includes(keyAppNative) || agent.includes(keyAppVersionIos) || agent.includes(keyAppVersionAndroid))
    )
    setIsLegacyAndroidApp(
      agent && agent.includes(keyAppNative) && !agent.includes(keyAppVersionAndroid) && agent.includes('Android')
    )
    setIsLegacyiOSApp(
      agent &&
        agent.includes(keyAppNative) &&
        !agent.includes(keyAppVersionIos) &&
        (agent.includes('iPhone') || agent.includes('iPad'))
    )
    setIsLatestAndroidApp(agent && agent.includes(keyAppVersionAndroid) && agent.includes('Android'))
    setIsLatestiOSApp(agent && agent.includes(keyAppVersionIos) && (agent.includes('iPhone') || agent.includes('iPad')))
    setIsCookieConsentRejected(agent && agent.includes(keyCookieConsentRejected) ? 'yes' : 'no')
    setIsSafari(agent && agent.match(/Safari/i) && !agent.match(/Chrome/i))
    setContextMounted(true)

    if (router.locale && (!readCookie('NEXT_LOCALE') || readCookie('NEXT_LOCALE') !== router.locale))
      createCookie('NEXT_LOCALE', router.locale, 30)
  }, [])

  return (
    <AppContext.Provider
      value={{
        contextMounted,
        isNativeApp,
        isLegacyAndroidApp,
        isLegacyiOSApp,
        isLatestAndroidApp,
        isLatestiOSApp,
        isCookieConsentRejected,
        isSafari,
        isRestricted,
        isExcluded
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

AppProvider.defaultProps = {
  loggedIn: false
}

AppProvider.propTypes = {
  loggedIn: PropTypes.bool,
  children: PropTypes.object
}

export default AppProvider
